/**
 * Guard against `NotFoundError: Failed to execute 'removeChild' / 'insertBefore'
 * on 'Node'` crashes caused by browser auto-translation (mainly Google Translate).
 *
 * Translate wraps text in `<font style="vertical-align: inherit;">` tags and swaps
 * text nodes out from under React. When React later unmounts/reorders, the node it
 * expects is gone, so `removeChild`/`insertBefore` throw — and because these fire
 * during React's commit phase, the whole app falls into the error boundary
 * ("Algo salió mal"). It hits many pages at once and reload doesn't help.
 *
 * Making these two DOM methods no-op when the node isn't actually a child of the
 * target stops the hard crash (the page keeps working, translated or not). This is
 * the well-known workaround for facebook/react#11538. Install once, before render.
 */
export function installDomTranslationGuard() {
  if (typeof Node === "undefined" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      if (typeof console !== "undefined") {
        console.warn(
          "[dom-translation-guard] Skipped removeChild: node is not a child of the expected parent (likely browser auto-translation).",
          child
        );
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== "undefined") {
        console.warn(
          "[dom-translation-guard] Skipped insertBefore: reference node is not a child of the expected parent (likely browser auto-translation).",
          referenceNode
        );
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
